import { RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button, ReceiptsFilters, ReceiptsTable, StatusMessage, Tabs, Workbench, panelId, tabId, type ReceiptsFiltersProps } from '@krp/ui-kit'

import { OneCQueryState } from '../../../shared/ui/OneCQueryState'
import '../../../shared/ui/onec.css'
import { CASH_RECEIPTS_MAX_DAYS } from '../model/selectors'
import type { CashCorrectionsVm, CashKindFilter, CashReceiptRowVm, CashReceiptsScreenActions, CashReceiptsScreenVm, CashReceiptsTab } from '../model/types'
import { CashCorrectionsTable } from './CashCorrectionsTable'
import { CashReceiptsPlacePicker } from './CashReceiptsPlacePicker'
import './receipts.css'

/**
 * То, чего нет во вкладке «Чеки» и что приносит только «Статистика по продажам»: итоги, график, прогноз и сообщения об их чтении.
 * Тип — срез свойств общих фильтров; этот файл сводок не знает и не импортирует.
 */
export type CashReceiptsExtras = Pick<ReceiptsFiltersProps, 'stats' | 'statsBusy' | 'notices' | 'trend' | 'periodTrend' | 'trendBusy' | 'todayBusy' | 'trendMessage'>

/** Табы вкладки «Чеки»: «Чеки» | «Чеки коррекции» (RP-ST15). Выбранный таб — в адресе, его держит binding. */
export interface CashReceiptsTabs {
  tab: CashReceiptsTab
  onTab: (tab: CashReceiptsTab) => void
  corrections: CashCorrectionsVm
}

const TABS_PREFIX = 'onec-receipts'

/**
 * «Чеки 1С»: чеки ККМ и возвраты базы 1С, как их ведёт 1С. Слева — дерево «Где смотрим» (организация → магазин или склад
 * → касса), справа — дни текущей недели, крупная сводка (приход, возвраты, итог), отбор по виду и поиск, а ниже сами чеки:
 * новые сверху, старые снизу; нажатие на чек раскрывает его товары, «Подробнее» открывает окно чека. Только чтение: свои продажи пробивает касса своего источника. Вид документа — словом и
 * значком, а не цветом. 1С не ответила — её текст и «Повторить», а не пустой список.
 */
export function CashReceiptsScreen({ vm, actions, extras = {}, receipts = true, tabs, receiptLines }: {
  vm: CashReceiptsScreenVm
  actions: CashReceiptsScreenActions
  extras?: CashReceiptsExtras
  /** Список чеков и поиск по номеру; у «Статистики по продажам» их нет — там только итоги и графики, чеки не читаются. */
  receipts?: boolean
  /** Табы «Чеки» | «Чеки коррекции»; нет — табов нет, экран показывает чеки. */
  tabs?: CashReceiptsTabs
  /** Товары чека под раскрытой строкой (их читает binding); нет — строки не раскрываются. */
  receiptLines?: (row: CashReceiptRowVm) => ReactNode
}) {
  const nav = vm.nav
  const { notices: extraNotices, ...summaries } = extras
  const tab: CashReceiptsTab = tabs?.tab ?? 'receipts'
  const corrections = tabs !== undefined && tab === 'corrections' ? tabs.corrections : null
  // В чеках коррекции кассир — подпись строкой, а вид и поиск — отборы чеков: к коррекциям они не относятся и не показываются.
  const correctionsFilters = { cashierControl: null }
  const tabStrip = tabs === undefined ? undefined : (
    <div className="onec-receipts__tabs">
      <Tabs label="Чеки базы 1С" idPrefix={TABS_PREFIX} items={[{ id: 'receipts', label: 'Чеки' }, { id: 'corrections', label: 'Чеки коррекции' }]}
        selected={tab} onSelect={(id) => tabs.onTab(id === 'corrections' ? 'corrections' : 'receipts')} />
    </div>
  )
  const receiptsList = (
    <>
      {receipts && vm.mode === 'range' && vm.query.status === 'ready' && vm.periodError === null && (
        <p className="onec-field__note">
          За период из нескольких дней показана только сводка — чеки не выгружаются. Чтобы увидеть чеки, выберите один день: на графике.
        </p>
      )}
      {vm.periodError !== null ? (
        <StatusMessage kind="empty" title={vm.periodError} />
      ) : (
        receipts && <OneCQueryState query={vm.query} emptyText="За период чеков нет" onRetry={actions.retry} />
      )}
      {receipts && vm.filteredOut && <StatusMessage kind="empty" title="По этому отбору чеков нет" text="Число справа от названия в дереве — сколько чеков у места за период. Выберите место, где оно не ноль, или смените вид чека, поиск, период." />}
      {receipts && vm.rows.length > 0 && (
        <ReceiptsTable label="Чеки базы" rows={vm.rows} onDetails={actions.openReceipt} {...(receiptLines === undefined ? {} : { renderLines: receiptLines })} columns={[
          { id: 'shift', label: 'Смена', cell: (row) => row.shift, title: (row) => row.shift },
          { id: 'status', label: 'Состояние', cell: (row) => row.status, title: (row) => row.status },
        ]} />
      )}
    </>
  )
  return (
    <section className="onec-screen" aria-label="Журнал чеков базы">
      {nav.note !== null && <p className="onec-field__note">{nav.note}</p>}
      <Workbench
        sideTitle="Где смотрим"
        sideSummary={nav.summaryText}
        side={
          <>
            <Button variant="secondary" data-size="sm" icon={<RefreshCw size={16} aria-hidden="true" />} busy={vm.refreshing} onClick={actions.refresh}>
              Обновить всю базу
            </Button>
            {nav.query.status === 'ready' ? (
              <CashReceiptsPlacePicker nav={nav} onSelect={actions.nodeChosen} onToggle={actions.nodeToggled} />
            ) : (
              <OneCQueryState query={nav.query} emptyText="В базе нет касс ККМ" onRetry={actions.retry} />
            )}
          </>
        }
      >
        <div className="onec-toolbar krp-sticky-refresh">
          <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} busy={vm.refreshing} onClick={actions.refresh}>
            Обновить
          </Button>
          {vm.readAt !== null && <span className="onec-muted">{vm.readAt}</span>}
        </div>
        <ReceiptsFilters
          today={vm.today}
          period={vm.period}
          onPeriod={actions.periodChanged}
          maxDays={CASH_RECEIPTS_MAX_DAYS}
          allTimeFrom={vm.allTimeFrom}
          dataFrom={vm.firstDay}
          {...summaries}
          {...(tabStrip === undefined ? {} : { tabs: tabStrip })}
          notices={
            <>
              {extraNotices}
              {corrections === null && vm.truncatedNote !== null && <StatusMessage kind="empty" title="Показаны не все чеки" text={vm.truncatedNote} />}
            </>
          }
          {...(corrections === null ? {} : correctionsFilters)}
          kinds={corrections === null ? vm.kinds : []}
          kind={vm.kind}
          onKind={(kind) => actions.kindChanged(kind as CashKindFilter)}
          cashiers={vm.cashiers}
          cashier={vm.cashier}
          onCashier={actions.cashierChanged}
          cashierHint="В чеках этой базы кассир не указан или его нет в справочнике пользователей: выбирать некого."
          searchShown={corrections === null && receipts && vm.mode !== 'range'}
          search={vm.search}
          searchLabel="Поиск по номеру, кассе, сумме"
          onSearch={actions.searchChanged}
          days={vm.days}
          onDay={(day) => actions.periodChanged({ from: day, to: day })}
        />
        {tabs === undefined ? receiptsList : (
          <div className="onec-receipts__panel" role="tabpanel" id={panelId(TABS_PREFIX, tab)} aria-labelledby={tabId(TABS_PREFIX, tab)}>
            {corrections !== null ? <CashCorrectionsTable vm={corrections} onRetry={actions.retry} /> : receiptsList}
          </div>
        )}
      </Workbench>
    </section>
  )
}
